"use client";

import * as React from "react";
import clsx from "clsx";
import { LazyMotion, domAnimation, m, type MotionProps } from "framer-motion";
import type { BenefitCardVm, BenefitsCtaVm } from "@/lib/benefits-section";
import type { ServiceBenefitsGridVariant } from "@/components/ServiceBenefitsGrid";
import CtaButton from "@/components/ui/CtaButton";
import ButtonLandingPage from "@/components/landing/ButtonLandingPage";

type BaseProps = {
    heading?: string;
    description?: string;
    items?: BenefitCardVm[];
    cta?: BenefitsCtaVm | null;
    variant?: ServiceBenefitsGridVariant;
    overlapClassName?: string;
    contentTopPaddingClassName?: string;
    useLandingButton?: boolean;
};

type ConflictingHtmlEvents =
    | "onAnimationStart"
    | "onAnimationEnd"
    | "onAnimationIteration"
    | "onDrag"
    | "onDragStart"
    | "onDragEnd"
    | "onDragEnter"
    | "onDragLeave"
    | "onDragOver"
    | "onDrop";

type SafeSectionHtmlProps = Omit<
    React.ComponentPropsWithoutRef<"section">,
    keyof BaseProps | keyof MotionProps | ConflictingHtmlEvents
>;

export type ServiceBenefitsGridClientProps = BaseProps & SafeSectionHtmlProps & MotionProps;

const slideUp = {
    hidden: { y: 30, opacity: 0 },
    visible: (i: number) => ({
        y: 0,
        opacity: 1,
        transition: {
            duration: 0.7,
            ease: [0.215, 0.61, 0.355, 1],
            delay: i * 0.1,
        },
    }),
} as const;

const VARIANT_CLASSES: Record<ServiceBenefitsGridVariant, { section: string; container: string }> = {
    "hero-overlap": {
        section: "-mt-12 md:-mt-58 lg:-mt-72 rounded-t-2xl rounded-bl-3xl",
        container: "pt-10 md:pt-32 lg:pt-96",
    },
    standalone: {
        section: "rounded-t-3xl",
        container: "",
    },
};

function BenefitCard({
    item,
    idx,
    layoutClass,
}: {
    item?: BenefitCardVm;
    idx: number;
    layoutClass: string;
}) {
    if (!item) return null;

    return (
        <m.div variants={slideUp} custom={idx} className={layoutClass}>
            <article
                className={[
                    "relative rounded-3xl border p-6",
                    "flex h-full min-h-[280px] flex-col justify-between overflow-hidden sm:min-h-[330px] md:min-h-[380px]",
                    "transition-shadow hover:shadow-[0_0_0_1px_rgba(255,255,255,0.06)]",
                    "border-secondary-light bg-secondary-light",
                ].join(" ")}
            >
                {item.number && (
                    <span className="pointer-events-none absolute right-5 top-5 text-primary uppercase">
                        {item.number}
                    </span>
                )}

                <div className="pr-11 md:pr-12">
                    {item.title && (
                        <h3 className="text-lg font-medium uppercase leading-snug md:text-2xl">
                            {item.title}
                        </h3>
                    )}
                </div>

                <div className="text-base">{item.text && <p>{item.text}</p>}</div>
            </article>
        </m.div>
    );
}

function BenefitCtaCard({
    cta,
    idx,
    useLandingButton = false,
}: {
    cta?: BenefitsCtaVm | null;
    idx: number;
    useLandingButton?: boolean;
}) {
    if (!cta) return null;

    return (
        <m.div variants={slideUp} custom={idx} className="lg:col-start-3 lg:row-start-2">
            <article
                className={[
                    "relative rounded-2xl border p-6",
                    "flex h-full flex-col overflow-hidden md:min-h-[380px]",
                    "transition-shadow hover:shadow-[0_0_0_1px_rgba(255,255,255,0.06)]",
                    "border-secondary-light bg-transparent",
                ].join(" ")}
            >
                {cta.title && <h3 className="mb-3 text-2xl font-medium md:mb-4">{cta.title}</h3>}

                <div className="mt-auto space-y-4">
                    {cta.text && <p className="text-base">{cta.text}</p>}
                    {useLandingButton ? (
                        <ButtonLandingPage label={cta.buttonLabel} />
                    ) : (
                        <CtaButton ctaLabel={cta.buttonLabel} />
                    )}
                </div>
            </article>
        </m.div>
    );
}

export default function ServiceBenefitsGridClient({
    heading,
    description,
    items = [],
    cta,
    variant = "hero-overlap",
    className,
    contentTopPaddingClassName,
    overlapClassName,
    useLandingButton = false,
    ...rest
}: ServiceBenefitsGridClientProps) {
    const i0 = items[0];
    const i1 = items[1];
    const i2 = items[2];
    const variantClasses = VARIANT_CLASSES[variant];

    return (
        <LazyMotion features={domAnimation}>
            <m.section
                id="relume"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.1 }}
                className={clsx(
                    "relative z-20 bg-secondary px-[5%] py-16 text-primary md:py-24 lg:py-28",
                    variantClasses.section,
                    overlapClassName,
                    className,
                )}
                {...rest}
            >
                <div className={clsx("container", variantClasses.container, contentTopPaddingClassName)}>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-6 lg:grid-cols-3 lg:gap-5">
                        <m.header variants={slideUp} custom={0} className="md:col-span-2 lg:col-span-2">
                            {heading && (
                                <h2 className="mb-4 max-w-[42rem] text-4xl font-medium leading-tight md:text-6xl">
                                    {heading}
                                </h2>
                            )}
                            {description && <p className="mb-8 max-w-md text-base">{description}</p>}
                        </m.header>

                        <BenefitCard item={i0} idx={1} layoutClass="lg:col-start-3 lg:row-start-1" />
                        <BenefitCard item={i1} idx={2} layoutClass="lg:col-start-1 lg:row-start-2" />
                        <BenefitCard item={i2} idx={3} layoutClass="lg:col-start-2 lg:row-start-2" />
                        <BenefitCtaCard cta={cta} idx={4} useLandingButton={useLandingButton} />
                    </div>
                </div>
            </m.section>
        </LazyMotion>
    );
}
